import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { MONETISEUP_LOGO } from "@/constants/brand";

const COUNTRIES = [
  "India", "United States", "United Kingdom", "Canada", "Australia", "Germany",
  "France", "Japan", "Singapore", "United Arab Emirates", "Brazil", "Mexico",
  "South Africa", "Netherlands", "Spain", "Italy", "Sweden", "Indonesia",
  "Philippines", "Vietnam", "Other",
];

const TRAFFIC = [
  "Under 100K monthly impressions",
  "100K – 1M monthly impressions",
  "1M – 10M monthly impressions",
  "10M – 50M monthly impressions",
  "50M – 200M monthly impressions",
  "200M+ monthly impressions",
];

export default function Signup() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    country: "",
    phone: "",
    email: "",
    website: "",
    traffic: "",
    comments: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.company.trim()) e.company = "Required";
    if (!form.phone.trim()) e.phone = "Required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Valid email required";
    if (!form.website.trim()) e.website = "Required";
    if (!form.traffic) e.traffic = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/signup`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        },
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      setSubmitted(true);
    } catch (e) {
      setSubmitError(
        "Something went wrong while submitting. Please try again or email us at contact@monetiseup.com.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <main className="relative min-h-screen grid place-items-center pt-32 pb-20 px-6">
        <div className="aurora-orb bg-[#0047FF] w-[600px] h-[600px] -top-40 left-1/2 -translate-x-1/2 opacity-30" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-strong rounded-3xl p-10 md:p-14 max-w-xl text-center"
        >
          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-[#0047FF] to-[#00F0FF] grid place-items-center mb-6">
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mb-3">
            Thanks, <span className="cyan-text">{form.name.split(" ")[0]}!</span>
          </h1>
          <p className="text-white/65 mb-8">
            We've received your signup request for{" "}
            <span className="text-white">{form.company}</span>. A specialist
            will reach you on{" "}
            <span className="text-white">{form.email}</span> within one business
            day.
          </p>
          <Link to="/" data-testid="signup-back-home" className="btn-primary">
            Back to Home <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="relative min-h-screen pt-32 pb-20">
      <div className="aurora-orb bg-[#0047FF] w-[600px] h-[600px] -top-40 -left-40 opacity-30" />
      <div className="aurora-orb bg-[#8A2BE2] w-[500px] h-[500px] bottom-0 right-0 opacity-25" />

      <div className="container-x relative grid lg:grid-cols-[1fr_1.2fr] gap-12 items-start">
        {/* Left — pitch */}
        <div>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-6">
            <Sparkles className="w-3.5 h-3.5 text-[#00F0FF]" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-white/80">
              Start Monetizing
            </span>
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] mb-6">
            Tell us about
            <br />
            <span className="aurora-text">your traffic.</span>
          </h1>
          <p className="text-white/65 leading-relaxed mb-8 max-w-md">
            Share a few details and our team will set up your account, integrate
            our ad technology, and start optimizing your revenue within 24–48
            hours.
          </p>
          <ul className="space-y-3 text-sm text-white/75">
            {[
              "Dedicated account manager",
              "NET 60 payouts, $100 minimum",
              "Premium demand · 98% fill rate",
              "Real-time analytics dashboard",
            ].map((b) => (
              <li key={b} className="flex items-center gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-[#00F0FF]" />
                {b}
              </li>
            ))}
          </ul>
        </div>

        {/* Right — form */}
        <form
          onSubmit={submit}
          data-testid="signup-form"
          className="glass-strong rounded-3xl p-7 md:p-10 space-y-5"
          noValidate
        >
          <Field
            label="Name"
            required
            error={errors.name}
            testid="signup-name"
            value={form.name}
            onChange={set("name")}
            placeholder="Your full name"
          />
          <Field
            label="Company Name"
            required
            error={errors.company}
            testid="signup-company"
            value={form.company}
            onChange={set("company")}
            placeholder="Acme Inc."
          />
          <div className="grid sm:grid-cols-2 gap-5">
            <SelectField
              label="Country"
              testid="signup-country"
              value={form.country}
              onChange={set("country")}
              options={COUNTRIES}
              placeholder="Select country"
            />
            <Field
              label="Phone Number"
              required
              error={errors.phone}
              testid="signup-phone"
              type="tel"
              value={form.phone}
              onChange={set("phone")}
              placeholder="+1 555 0100"
            />
          </div>
          <Field
            label="Email ID"
            required
            error={errors.email}
            testid="signup-email"
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="you@company.com"
          />
          <Field
            label="Website"
            required
            error={errors.website}
            testid="signup-website"
            type="url"
            value={form.website}
            onChange={set("website")}
            placeholder="https://yourcompany.com"
          />
          <SelectField
            label="Traffic Volume (Monthly)"
            required
            error={errors.traffic}
            testid="signup-traffic"
            value={form.traffic}
            onChange={set("traffic")}
            options={TRAFFIC}
            placeholder="Select range"
          />
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-white/55 mb-2">
              Comments
            </label>
            <textarea
              data-testid="signup-comments"
              rows={4}
              value={form.comments}
              onChange={set("comments")}
              placeholder="Anything we should know — current ad-tech setup, goals, timing…"
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#00F0FF]/50 focus:outline-none text-white placeholder:text-white/30 resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="signup-submit"
            className="btn-primary w-full justify-center text-base py-3 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? "Sending…" : "Sign Up"}{" "}
            {!submitting && <ArrowRight className="w-4 h-4" />}
          </button>

          {submitError && (
            <p
              data-testid="signup-error"
              className="text-sm text-red-400 text-center"
            >
              {submitError}
            </p>
          )}

          <p className="text-[11px] text-white/40 text-center pt-2">
            By signing up you agree to our{" "}
            <Link to="/terms-conditions" className="underline hover:text-white">
              Terms
            </Link>{" "}
            and{" "}
            <Link to="/privacy-policy" className="underline hover:text-white">
              Privacy Policy
            </Link>
            .
          </p>
        </form>
      </div>
    </main>
  );
}

function Field({ label, required, error, testid, ...rest }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] text-white/55 mb-2">
        {label}
        {required && <span className="text-[#00F0FF] ml-1">*</span>}
      </label>
      <input
        data-testid={testid}
        {...rest}
        className={`w-full px-4 py-3 rounded-xl bg-white/5 border focus:outline-none text-white placeholder:text-white/30 transition-colors ${
          error
            ? "border-red-400/60 focus:border-red-400"
            : "border-white/10 focus:border-[#00F0FF]/50"
        }`}
      />
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}

function SelectField({
  label,
  required,
  error,
  testid,
  value,
  onChange,
  options,
  placeholder,
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] text-white/55 mb-2">
        {label}
        {required && <span className="text-[#00F0FF] ml-1">*</span>}
      </label>
      <select
        data-testid={testid}
        value={value}
        onChange={onChange}
        className={`w-full px-4 py-3 rounded-xl bg-white/5 border focus:outline-none text-white transition-colors ${
          error
            ? "border-red-400/60 focus:border-red-400"
            : "border-white/10 focus:border-[#00F0FF]/50"
        } ${value ? "" : "text-white/30"}`}
      >
        <option value="" disabled className="bg-[#050B26]">
          {placeholder}
        </option>
        {options.map((o) => (
          <option key={o} value={o} className="bg-[#050B26] text-white">
            {o}
          </option>
        ))}
      </select>
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}
